import _ from 'underscore';

import {
  TaskData,
  WorksheetTaskLoadingStateMapping,
  WorksheetTaskStatusType,
} from 'notebook/worksheets/WorksheetConstants';

const WORKSHEET_TASK_STATUSES_TO_SHOW_ACTION_DROPDOWN = [
  WorksheetTaskStatusType.PENDING,
  WorksheetTaskStatusType.IN_PROGRESS,
];

export const BUTTON_DISABLED_PREV_RUN_NOT_COMPLETE =
  'Button is disabled because the previous unit operation has not been completed.';

export const BUTTON_DISABLED_ALL_TASKS_NOT_CREATED =
  'Button is disabled because this worksheet is still being compiled.';

export interface WorksheetControlVariables {
  isWorksheetPending: boolean;
  worksheetLastActionText: string | null;
  isWorksheetStatusBadgeDisabled: boolean;
  showWorksheetStatusDropdownButton: boolean;
  worksheetStatusBadgeTooltipText: string | null;
}

/**
 * Function to output booleans that control the UI behavior of WorksheetPageHeader & other worksheet-level components
 * @param worksheetTaskData task data of worksheet output by function WorksheetUtils.extractTaskData
 * @param hasUnsavedStepDocuments whether the worksheet still has step documents that have not been saved
 * @returns see interface GetWorksheetControlVariables
 */
export const getWorksheetControlVariables = (
  worksheetTaskData: TaskData,
  hasUnsavedStepDocuments: boolean,
  areAllMethodTasksCreated: boolean
): WorksheetControlVariables => {
  // We assume that the previous method is not completed when the worksheet task data is empty. However, there
  // is a period of time after the user has completed the prior method where the tasks are all asynchronously
  // being created. Since the window between no tasks created to the first task being created is small, it is a
  // close enough approximation for toggling the tooltip. However, there is a longer window after this till
  // all tasks are created. The worksheet should not be editable until all of the method tasks have been created.
  const prevMethodComplete = !_.isEmpty(worksheetTaskData);
  const isWorksheetPending =
    !worksheetTaskData.status ||
    worksheetTaskData.status === WorksheetTaskStatusType.PENDING ||
    !areAllMethodTasksCreated;

  // assemble text on page header describing worksheet's last action
  const worksheetLastActionVerb =
    worksheetTaskData.status === WorksheetTaskStatusType.PENDING
      ? null
      : worksheetTaskData.status === WorksheetTaskStatusType.IN_PROGRESS
        ? 'started'
        : worksheetTaskData.status?.toLowerCase();
  const worksheetLastActionText = worksheetLastActionVerb
    ? worksheetTaskData.status &&
      Object.values(WorksheetTaskLoadingStateMapping).includes(worksheetTaskData.status)
      ? `Run ${worksheetLastActionVerb}...`
      : `Run ${worksheetLastActionVerb} on ${worksheetTaskData.timestamp}`
    : null;

  // determine if WorksheetStatusBadge should be disabled
  const isWorksheetStatusBadgeDisabled =
    hasUnsavedStepDocuments ||
    !worksheetTaskData.status ||
    !prevMethodComplete ||
    !areAllMethodTasksCreated ||
    Object.values(WorksheetTaskLoadingStateMapping).includes(worksheetTaskData.status);

  // determine if the dropdown button for WorksheetStatusBadge should be shown
  // Include !prevMethodComplete here because we still want the dropdown to show up, in a
  // disabled state, when the previous unit operation has not yet been completed.
  const showWorksheetStatusDropdownButton = Boolean(
    !worksheetTaskData.status ||
      !prevMethodComplete ||
      WORKSHEET_TASK_STATUSES_TO_SHOW_ACTION_DROPDOWN.includes(worksheetTaskData.status)
  );
  // when worksheetStatusBadgeTooltipText is null we disable tooltip from showing
  const worksheetStatusBadgeTooltipText = prevMethodComplete
    ? areAllMethodTasksCreated
      ? null
      : BUTTON_DISABLED_ALL_TASKS_NOT_CREATED
    : BUTTON_DISABLED_PREV_RUN_NOT_COMPLETE;

  return {
    isWorksheetPending,
    worksheetLastActionText,
    isWorksheetStatusBadgeDisabled,
    showWorksheetStatusDropdownButton,
    worksheetStatusBadgeTooltipText,
  };
};
